import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { COUNTING_ENGINE_THEME, countingSpokenWord, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import { BigCounter, CornerPill, COUNTING_PANEL, CountingPanel, CountingPiece, CountingRoot, POP_SPRING, hexAlpha, useCompleteOnce } from "../CountingPiece";

/** The empty seat: the same star, faded and desaturated, so the one that lands matches it exactly. */
function StarSeat({ size }) {
  return (
    <span aria-hidden="true" style={{ position: "absolute", inset: "4%", display: "inline-flex", filter: "grayscale(1) brightness(1.15)", opacity: 0.32 }}>
      <CountingPiece asset="star" variant="bare" size="100%" floats={false} />
    </span>
  );
}

/** The row of stars above the instrument: one lands, with a pop, per beat. */
function StarRow({ total, lit, reduceMotion }) {
  const seat = "clamp(30px, calc(var(--tile) * 0.62), 68px)";
  return (
    <div
      aria-label={`${lit} of ${total} stars`}
      style={{
        display: "grid", gridTemplateColumns: `repeat(auto-fit, ${seat})`, justifyContent: "center", gap: "calc(var(--tile) * 0.08)",
        padding: "calc(var(--tile) * 0.08) calc(var(--tile) * 0.16)", alignSelf: "center", maxWidth: "100%", boxSizing: "border-box",
        ...COUNTING_PANEL, borderRadius: 999,
      }}
    >
      {Array.from({ length: total }, (_, i) => {
        const on = i < lit;
        return (
          <span key={i} style={{ position: "relative", width: seat, height: seat, display: "grid", placeItems: "center" }}>
            <StarSeat size={seat} />
            <AnimatePresence>
              {on && (
                <motion.span
                  key="lit"
                  initial={reduceMotion ? false : { scale: 0.2, y: 26, opacity: 0, rotate: -30 }}
                  animate={{ scale: 1, y: 0, opacity: 1, rotate: 0 }}
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 16 }}
                  style={{ position: "absolute", inset: "4%", display: "inline-flex" }}
                >
                  <CountingPiece asset="star" variant="bare" size="100%" floats phase={i} reduceMotion={reduceMotion} />
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Counting things you can't hold: every tap on the drum (or the rabbit) is
 * one beat, one star arrives, one number word is spoken.
 */
const CountEventsGame = ({ activity, speak, sfx, onComplete, reduceMotion = false }) => {
  const target = countingTarget(activity);
  const hero = activity.number === 36 ? "rabbit" : "drum";
  const accent = COUNTING_ENGINE_THEME.countEvents.accent;
  const [count, setCount] = useState(0);
  const [beat, setBeat] = useState(0);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const done = count >= target;

  const add = () => {
    if (done) return;
    const next = count + 1;
    setCount(next);
    setBeat((b) => b + 1);
    speak?.(countingSpokenWord(next), { interrupt: true });
    if (next === target) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      complete();
    } else {
      sfx?.("pop", { rate: hero === "drum" ? 0.85 : 1.1 });
    }
  };

  const heroMotion = reduceMotion
    ? {}
    : hero === "rabbit"
      ? { animate: { y: [0, -34, 0], scaleY: [1, 1.08, 0.94, 1] }, transition: { duration: 0.42, ease: "easeOut" } }
      : { animate: { scale: [1, 0.9, 1.06, 1], rotate: [0, -3, 3, 0] }, transition: { duration: 0.36, ease: "easeOut" } };

  // The instrument is the whole scene: a big disc, capped so it never crowds the panel.
  const disc = "min(calc(var(--tile) * 3.3), 62vmin, calc(var(--arena-h, 640px) * 0.6), calc(var(--arena-w, 760px) * 0.72))";
  const heroLabel = hero === "drum" ? "Tap the drum" : "Tap the rabbit";

  return (
    <CountingRoot>
      <StarRow total={target} lit={count} reduceMotion={reduceMotion} />

      <CountingPanel grow tint={accent} corner={<CornerPill>{count} of {target}</CornerPill>}>
        <div style={{ position: "relative", display: "grid", placeItems: "center", width: disc, height: disc, maxWidth: "100%" }}>
          <AnimatePresence>
            {!reduceMotion && beat > 0 && [0, 1].map((ring) => (
              <motion.span
                key={`${beat}-${ring}`}
                aria-hidden="true"
                initial={{ scale: 0.8, opacity: 0.7 }}
                animate={{ scale: 1.55 + ring * 0.25, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.65 + ring * 0.15, ease: "easeOut", delay: ring * 0.08 }}
                style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `calc(var(--tile) * 0.05) solid ${accent}`, pointerEvents: "none" }}
              />
            ))}
          </AnimatePresence>
          <motion.button
            type="button"
            className="counting-btn"
            aria-label={heroLabel}
            onClick={add}
            whileTap={reduceMotion || done ? undefined : { scale: 0.94, y: 4 }}
            whileHover={reduceMotion || done ? undefined : { scale: 1.03, y: -3 }}
            initial={reduceMotion ? false : { scale: 0 }}
            animate={{ scale: 1 }}
            transition={POP_SPRING}
            style={{
              width: "100%", height: "100%", borderRadius: "50%",
              border: `calc(var(--tile) * 0.05) solid ${hexAlpha(accent, 0.6)}`,
              background: `radial-gradient(circle at 50% 38%, ${LEARN.color.white} 0%, ${hexAlpha(accent, 0.14)} 62%, ${hexAlpha(accent, 0.3)} 100%)`,
              boxShadow: `inset 0 calc(var(--tile) * -0.08) 0 ${hexAlpha(accent, 0.22)}, 0 calc(var(--tile) * 0.09) 0 ${hexAlpha(accent, 0.55)}, ${LEARN.shadow.float}`,
              cursor: done ? "default" : "pointer",
              display: "grid", placeItems: "center", padding: 0, touchAction: "manipulation", position: "relative",
            }}
          >
            <motion.span key={beat} style={{ display: "inline-flex", width: "76%", height: "76%" }} {...heroMotion}>
              <CountingPiece asset={hero} variant="bare" size="100%" reduceMotion={reduceMotion} />
            </motion.span>
          </motion.button>
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <BigCounter value={count} total={target} accent={accent} placeholder={heroLabel} />
    </CountingRoot>
  );
};

export default CountEventsGame;
